import React, { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Plus, CalendarDays, DollarSign, Trash, ArrowUp, ArrowDown, Filter, Infinity, CheckCircle2, XCircle, Pencil } from "lucide-react";
import { useAppToast } from "@/hooks/useAppToast";
import { Promotion, CreatePromotionInput, UpdatePromotionInput } from "@/services/admin/packages.interfaces";
import CreatePromotionDialog from "./CreatePromotionDialog";
import FilterDialog from "./FilterDialog";
import DeletePromotionDialog from "./DeletePromotionDialog";
import EditPromotionDialog from "./EditPromotionDialog";
import { Badge } from "@/components/ui/badge";

interface PromotionsTableProps {
  promotions: Promotion[];
  onDelete: (promotionId: string) => Promise<void>;
  onAdd: (promotion: CreatePromotionInput) => Promise<void>;
  onEdit?: (promotionId: string, data: UpdatePromotionInput) => Promise<void>;
}

type SortField = 'date' | 'price';
type SortDirection = 'asc' | 'desc';

const formatDate = (date: Date | null) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatPrice = (price: number) => {
  return price.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
};

const getPromotionPeriodText = (startDate: Date | null, endDate: Date | null) => {
  if (!startDate && !endDate) {
    return "Período contínuo";
  }
  if (!startDate) {
    const formattedEndDate = formatDate(endDate);
    return formattedEndDate ? `Até ${formattedEndDate}` : "Período contínuo";
  }
  if (!endDate) {
    const formattedStartDate = formatDate(startDate);
    return formattedStartDate ? `A partir de ${formattedStartDate}` : "Período contínuo";
  }
  const formattedStartDate = formatDate(startDate);
  const formattedEndDate = formatDate(endDate);
  return (formattedStartDate && formattedEndDate) 
    ? `${formattedStartDate} - ${formattedEndDate}` 
    : "Período contínuo";
};

const isPromotionActive = (promotion: Promotion) => {
  if (!(promotion.active ?? promotion.isActive)) return false;
  
  const now = new Date();
  if (promotion.startDate && new Date(promotion.startDate) > now) return false;
  if (promotion.endDate && new Date(promotion.endDate) < now) return false;
  
  return true;
};

const getPromotionStatusText = (promotion: Promotion) => {
  if (!(promotion.active ?? promotion.isActive)) return "Inativa";
  
  const now = new Date();
  if (promotion.startDate && new Date(promotion.startDate) > now) {
    return "Aguardando início";
  }
  if (promotion.endDate && new Date(promotion.endDate) < now) {
    return "Encerrada";
  }
  
  return "Ativa";
};

const getPromotionStatusVariant = (promotion: Promotion) => {
  if (!(promotion.active ?? promotion.isActive)) return "secondary";
  
  const now = new Date();
  if (promotion.startDate && new Date(promotion.startDate) > now) {
    return "warning";
  }
  if (promotion.endDate && new Date(promotion.endDate) < now) {
    return "destructive";
  }
  
  return "success";
};

const PromotionsTable: React.FC<PromotionsTableProps> = ({
  promotions,
  onDelete,
  onAdd,
  onEdit,
}) => {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [promotionToDelete, setPromotionToDelete] = useState<Promotion | null>(null);
  const [promotionToEdit, setPromotionToEdit] = useState<Promotion | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const toast = useAppToast();

  // Sorting states
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Filter states
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');

  const handleCreatePromotion = async (data: CreatePromotionInput) => {
    setIsCreating(true);
    try {
      await onAdd(data);
      setCreateDialogOpen(false);
      toast.success("Promoção criada com sucesso");
    } catch (error) {
      console.error("Error creating promotion:", error);
      toast.error("Erro ao criar promoção");
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteClick = (promotion: Promotion) => {
    setPromotionToDelete(promotion);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!promotionToDelete) return;
    
    setIsDeleting(true);
    try {
      await onDelete(promotionToDelete.id);
      setDeleteDialogOpen(false);
      setPromotionToDelete(null);
      toast.success("Promoção removida com sucesso");
    } catch (error) {
      console.error("Error deleting promotion:", error);
      toast.error("Erro ao remover promoção");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditClick = (promotion: Promotion) => {
    setPromotionToEdit(promotion);
    setEditDialogOpen(true);
  };

  const handleEditPromotion = async (id: string, data: UpdatePromotionInput) => {
    if (!onEdit) return;
    
    setIsEditing(true);
    try {
      await onEdit(id, data);
      setEditDialogOpen(false);
      setPromotionToEdit(null);
      toast.success("Promoção atualizada com sucesso");
    } catch (error) {
      console.error("Error updating promotion:", error);
      toast.error("Erro ao atualizar promoção");
    } finally {
      setIsEditing(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const clearFilters = () => {
    setStartDateFilter('');
    setEndDateFilter('');
  };

  const hasActiveFilters = startDateFilter || endDateFilter;
  const activeFiltersCount = [startDateFilter, endDateFilter].filter(Boolean).length;

  const filteredAndSortedPromotions = useMemo(() => {
    let result = [...promotions];

    // Apply date filters
    if (startDateFilter) {
      const filterStart = new Date(startDateFilter);
      result = result.filter(promo => {
        if (!promo.startDate) return true; // Include promotions with no start date
        return new Date(promo.startDate) >= filterStart;
      });
    }
    if (endDateFilter) {
      const filterEnd = new Date(endDateFilter);
      result = result.filter(promo => {
        if (!promo.endDate) return true; // Include promotions with no end date
        return new Date(promo.endDate) <= filterEnd;
      });
    }

    // Apply sorting
    result.sort((a, b) => {
      if (sortField === 'date') {
        // Handle null dates in sorting
        if (!a.startDate && !b.startDate) return 0;
        if (!a.startDate) return 1;
        if (!b.startDate) return -1;
        const dateA = new Date(a.startDate).getTime();
        const dateB = new Date(b.startDate).getTime();
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      } else {
        return sortDirection === 'asc' ? a.price - b.price : b.price - a.price;
      }
    });

    return result;
  }, [promotions, sortField, sortDirection, startDateFilter, endDateFilter]);

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ArrowUp className="h-4 w-4 text-blue-500" />
    ) : (
      <ArrowDown className="h-4 w-4 text-blue-500" />
    );
  };

  const getFilterSummary = () => {
    if (!hasActiveFilters) return null;

    if (startDateFilter && endDateFilter) {
      return `${formatDate(new Date(startDateFilter))} - ${formatDate(new Date(endDateFilter))}`;
    }
    if (startDateFilter) {
      return `A partir de ${formatDate(new Date(startDateFilter))}`;
    }
    if (endDateFilter) {
      return `Até ${formatDate(new Date(endDateFilter))}`;
    }
  };

  return (
    <section className="mb-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-neutral-900">Promoções</h2>
        <Button variant="default" onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" /> Adicionar Promoção
        </Button>
      </div>

      <CreatePromotionDialog
        isOpen={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSubmit={handleCreatePromotion}
        isSubmitting={isCreating}
      />

      <FilterDialog
        isOpen={filterDialogOpen}
        onOpenChange={setFilterDialogOpen}
        startDate={startDateFilter}
        endDate={endDateFilter}
        onStartDateChange={setStartDateFilter}
        onEndDateChange={setEndDateFilter}
        onClearFilters={clearFilters}
      />

      <DeletePromotionDialog
        isOpen={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
        promotion={promotionToDelete}
      />

      {onEdit && (
        <EditPromotionDialog
          isOpen={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          onSubmit={handleEditPromotion}
          isSubmitting={isEditing}
          promotion={promotionToEdit}
        />
      )}

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Button
                variant={hasActiveFilters ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterDialogOpen(true)}
                className="flex items-center gap-2 min-w-[100px]"
              >
                <Filter className="h-4 w-4" />
                Filtrar
                {hasActiveFilters && (
                  <span className="ml-1 bg-white bg-opacity-20 text-xs py-0.5 px-2 rounded-full">
                    {activeFiltersCount}
                  </span>
                )}
              </Button>
              {hasActiveFilters && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-600">Período:</span>
                  <span className="font-medium text-gray-900">{getFilterSummary()}</span>
                </div>
              )}
            </div>
            <div className="text-sm text-gray-500">
              {filteredAndSortedPromotions.length} {filteredAndSortedPromotions.length === 1 ? 'promoção' : 'promoções'} {hasActiveFilters ? 'encontrada(s)' : 'no total'}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant={sortField === 'date' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleSort('date')}
              className="flex items-center gap-1"
            >
              Ordenar por Data
              <SortIcon field="date" />
            </Button>
            <Button
              variant={sortField === 'price' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleSort('price')}
              className="flex items-center gap-1"
            >
              Ordenar por Valor
              <SortIcon field="price" />
            </Button>
          </div>
        </div>

        <ul className="divide-y divide-gray-100">
          {filteredAndSortedPromotions.map((promotion) => (
            <li key={promotion.id} className="p-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-center justify-between group">
                <div className="flex items-center">
                  <div className="bg-blue-50 p-2 rounded-lg">
                    {!promotion.startDate && !promotion.endDate ? (
                      <Infinity className="w-5 h-5 text-blue-500" />
                    ) : (
                      <CalendarDays className="w-5 h-5 text-blue-500" />
                    )}
                  </div>
                  <div className="ml-3">
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-gray-600">Período</p>
                      <Badge variant={getPromotionStatusVariant(promotion)}>
                        {isPromotionActive(promotion) ? (
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                        ) : (
                          <XCircle className="w-3 h-3 mr-1" />
                        )}
                        {getPromotionStatusText(promotion)}
                      </Badge>
                      {promotion.isRecurring ? (
                        <Badge variant="outline">Assinatura</Badge>
                      ) : null}
                    </div>
                    <p className="font-semibold text-gray-900">
                      {getPromotionPeriodText(promotion.startDate, promotion.endDate)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="bg-green-50 p-2 rounded-lg mr-3">
                    <DollarSign className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Valor</p>
                    <p className="font-semibold text-gray-900">
                      {formatPrice(promotion.price)}
                      {promotion.isRecurring && promotion.recurrenceIntervalMonths
                        ? ` / ${promotion.recurrenceIntervalMonths}m`
                        : ""}
                    </p>
                  </div>
                  <div className="flex ml-4">
                    {onEdit && (
                      <Button
                        variant="ghost"
                        onClick={() => handleEditClick(promotion)}
                        className="mr-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 opacity-0 group-hover:opacity-100 transition-all duration-200"
                        title="Editar promoção"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      onClick={() => handleDeleteClick(promotion)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all duration-200"
                      title="Excluir promoção"
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </li>
          ))}
          {filteredAndSortedPromotions.length === 0 && (
            <li className="text-center text-neutral-500 py-8">
              {hasActiveFilters ? 'Nenhuma promoção encontrada com os filtros atuais' : 'Nenhuma promoção cadastrada'}
            </li>
          )}
        </ul>
      </div>
    </section>
  );
};

export default PromotionsTable;